import { useResponse } from 'contexts/response';
import { useCallback } from 'react';

import { BillableEvent } from 'types';

const BASE_URL = new URL('/api/billableEvents', window.location.origin);

export const useBillableEventsApi = () => {
  const { handleError, setSuccess } = useResponse();

  const deleteBillableEvent = useCallback(
    async (id: number): Promise<number | null> => {
      const DELETE_INIT: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const response = await fetch(`${BASE_URL}/${id}`, DELETE_INIT);
      if (response.ok) {
        setSuccess(`Billable Event ${id} deleted.`);
        return id;
      }
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          const json = await response.json();
          handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    },
    [handleError, setSuccess]
  );

  const postBillableEvent = useCallback(
    async (billableEvent: BillableEvent): Promise<BillableEvent | null> => {
      const body = { ...billableEvent };
      if (!!billableEvent.unit) {
        body.unit = { id: billableEvent.unit.id };
      }
      if (!!billableEvent.associatedUnit) {
        body.associatedUnit = { id: billableEvent.associatedUnit.id };
      }
      const POST_INIT: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(body)
      };

      try {
        const response = await fetch(`${BASE_URL}`, POST_INIT);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess(`Billable Event ${json.id} created!`);
          return json;
        }

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const putBillableEvent = useCallback(
    async (billableEvent: BillableEvent, id: number): Promise<BillableEvent | null> => {
      const body = { ...billableEvent };
      if (!!billableEvent.unit) {
        body.unit = { id: billableEvent.unit.id };
      }
      if (!!billableEvent.associatedUnit) {
        body.associatedUnit = { id: billableEvent.associatedUnit.id };
      }

      const PUT_INIT: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(body)
      };

      try {
        const response = await fetch(`${BASE_URL}/${id}`, PUT_INIT);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess(`Billable Event ${json.id} updated!`);
          return json;
        }

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  return { deleteBillableEvent, postBillableEvent, putBillableEvent };
};
